import * as React from "react";
import {
  ScrollView,
  StatusBar,
  StyleSheet,
  Pressable,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
} from "react-native";
import Colors from "../../constants/Colors";
import { View, Text } from "../../components/Themed";
import PageHeader from "../../components/PageHeader";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import GlobalContext from "../../context/Global";
import QrSignIn from "../QrSignIn/index.web";
import openhouseAPI, { setCheckInKey } from "../../services/openhouse";
import LiteQRSignIn from "../QrSignIn/liteSignIn.web";

const setLocalCheckInParams = (
  id: string | null,
  team: string | null,
  phone: string | null
) => {
  if (Platform.OS === "web") {
    if (id) localStorage.setItem("qr_checkin_id", id);
    if (team) localStorage.setItem("qr_checkin_team", team);
    if (phone) localStorage.setItem("qr_checkin_phone", phone);
  }
};

const getLocalCheckInParams = () => {
  if (Platform.OS === "web") {
    return {
      id: localStorage.getItem("qr_checkin_id"),
      team: localStorage.getItem("qr_checkin_team"),
      phone: localStorage.getItem("qr_checkin_phone"),
    };
  }
  return { id: null, team: null, phone: null };
};

type propertyDetails = {
  city: string;
  country: string;
  postal_code: string;
  state: string;
  street_address: string;
  uuid: string;
};

export const QrCheckIn = ({ route, navigation }: any) => {
  const routeParams = route.params || {};
  const storedParams = getLocalCheckInParams();

  const id = routeParams.id || storedParams.id;
  const team = routeParams.team || storedParams.team;
  const phone = routeParams.phone || storedParams.phone;

  const formMethods = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      address: "",
    },
  });
  const { isTablet: deviceIsTablet } = React.useContext(GlobalContext);
  const { width } = useWindowDimensions();
  const isTablet = deviceIsTablet || width >= 768;
  const [currentStep, setCurrentStep] = useState(0);
  const [change, setChange] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [questions, setQuestions] = useState<any>(null);
  const [propertyDetails, setPropertyDetails] =
    useState<propertyDetails | null>(null);

  useEffect(() => {
    if (routeParams.id || routeParams.team || routeParams.phone) {
      setLocalCheckInParams(
        routeParams.id,
        routeParams.team,
        routeParams.phone
      );
    }
    if (id) {
      setCheckInKey(id);
    }
  }, [routeParams.id, routeParams.team, routeParams.phone, id]);

  useEffect(() => {
    if (!id) {
      navigation.replace("OpenHouseError");
    }
  }, [id, navigation]);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await openhouseAPI.get(
          `/checkin/form/questionsAndAnswers`
        );
        const propertyDetails = await openhouseAPI.get(
          `/checkin/propertyDetails`
        );
        setPropertyDetails(propertyDetails.data?.data?.propertyDetails);
        setQuestions(response.data?.data);
      } catch (err: any) {
        if (err.response?.status === 412) {
          navigation.replace("OpenHouseAlreadyCompleted");
        } else {
          console.error("Error fetching questions:", err);
          navigation.replace("OpenHouseError");
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  const restartForm = () => {
    if (currentStep === 0) setChange(!change);
    setCurrentStep(0);
  };

  const RestartFormButton = () => (
    <Pressable style={styles.restartFormText} onPress={() => restartForm()}>
      <Text
        size={20}
        lineHeight={28}
        weight={"SemiBold"}
        lightColor={Colors.primary_500}
        darkColor={Colors.primary_500}
      >
        Restart Form
      </Text>
    </Pressable>
  );

  const PageTitleContainer = (props: any) => (
    <View style={styles.pageTitleContainer}>{props.children}</View>
  );

  const PageTitle = () => (
    <Text
      size={18}
      lineHeight={24}
      weight="SemiBold"
      lightColor={Colors.nuetral_grey_700}
    >
      Open House Sign In
    </Text>
  );

  const HeaderContainer = (props: any) => (
    <View
      style={[
        styles.headerContainer,
        {
          flexDirection: isTablet ? "row" : "column",
        },
      ]}
    >
      {props.children}
    </View>
  );

  const HeaderTextContainer = (props: any) => (
    <View style={styles.headerText}>{props.children}</View>
  );

  const Address = (props: any) => (
    <Text
      size={16}
      lineHeight={20}
      lightColor={Colors.nuetral_grey_700}
      style={{ padding: 16 }}
    >
      {propertyDetails?.street_address}, {propertyDetails?.city},{" "}
      {propertyDetails?.state} {propertyDetails?.postal_code}
    </Text>
  );

  const RestartFormButtonContainer = (props: any) => (
    <View
      style={[
        styles.restartButtonContainer,
        { paddingBottom: !isTablet ? 34 : 0 },
      ]}
    >
      {props.children}
    </View>
  );

  return (
    <FormProvider {...formMethods}>
      <View
        lightColor={Colors.background}
        darkColor={Colors.nuetral_grey_600}
        style={styles.container}
      >
        <StatusBar hidden={true} />
        <PageHeader
          centerComponent={
            <PageTitleContainer>
              <PageTitle />
            </PageTitleContainer>
          }
        ></PageHeader>
        {isLoading ? (
          <View
            style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
          >
            <ActivityIndicator size="large" color={Colors.primary_500} />
          </View>
        ) : (
          <ScrollView
            keyboardShouldPersistTaps="always"
            showsVerticalScrollIndicator={false}
          >
            <HeaderContainer>
              {isTablet && <RestartFormButton />}
              <HeaderTextContainer>
                {propertyDetails && <Address />}
              </HeaderTextContainer>
              <View style={{ flex: 1 }}></View>
            </HeaderContainer>
            <View
              lightColor={Colors.base_colors_white}
              darkColor={Colors.nuetral_grey_700}
              style={[styles.signInFormContainer]}
            >
              {questions && questions.length === 1 ? (
                <LiteQRSignIn
                  questions={questions}
                  step={currentStep}
                  setStep={setCurrentStep}
                  change={change}
                  team={team}
                  phone={phone}
                  checkInKey={id}
                />
              ) : (
                <QrSignIn
                  questions={questions}
                  step={currentStep}
                  setStep={setCurrentStep}
                  change={change}
                  team={team}
                  phone={phone}
                  checkInKey={id}
                />
              )}
            </View>
            {!isTablet && (
              <RestartFormButtonContainer>
                <RestartFormButton />
              </RestartFormButtonContainer>
            )}
          </ScrollView>
        )}
      </View>
    </FormProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerContainer: {
    paddingHorizontal: 24,
  },
  pageTitleContainer: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  progressBarContainer: {
    paddingHorizontal: 50,
  },
  restartButtonContainer: {
    alignItems: "center",
    paddingTop: 32,
  },

  signInFormContainer: {
    paddingVertical: 40,
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
  },
  houseNumbers: {
    flexDirection: "row",
    gap: 16,
  },
  signInForm: {
    width: "100%",
    gap: 32,
  },
  form: {
    width: "100%",
    paddingHorizontal: 24,
    gap: 32,
  },
  signInFormHeader: {
    alignItems: "center",
    gap: 16,
  },
  signInFormControls: {
    gap: 16,
    paddingHorizontal: 64,
  },
  qrCodeContainer: {
    alignSelf: "center",
    marginTop: 32,
    paddingHorizontal: 30,
    paddingVertical: 40,
    width: "70%",
    flexDirection: "row",
    borderWidth: 1,
    borderColor: Colors.nuetral_grey_400,
  },
  qrCodeContainerText: {
    alignItems: "center",
    flex: 0.7,
    gap: 16,
  },
  qRCodeInstructions: {
    textAlign: "center",
  },
  qrCodeContainerImage: {
    flex: 0.3,
    alignItems: "center",
    justifyContent: "center",
  },
  headerText: {
    alignItems: "center",
    gap: 3,
  },
  restartFormText: {
    flex: 1,
  },
});
